import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Star } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { cn } from "@/lib/utils";

interface RateProviderDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  sessionId: string;
  providerId: string;
  providerName: string;
  existingRating?: { rating: number; comment: string | null } | null;
}

export function RateProviderDialog({
  open,
  onOpenChange,
  sessionId,
  providerId,
  providerName,
  existingRating,
}: RateProviderDialogProps) {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [rating, setRating] = useState(existingRating?.rating || 0);
  const [hoveredStar, setHoveredStar] = useState(0);
  const [comment, setComment] = useState(existingRating?.comment || "");

  const submitRating = useMutation({
    mutationFn: async () => {
      if (!user || rating === 0) return;

      if (existingRating) {
        const { error } = await supabase
          .from("provider_ratings" as any)
          .update({ rating, comment: comment.trim() || null } as any)
          .eq("session_id", sessionId)
          .eq("member_id", user.id);
        if (error) throw error;
      } else {
        const { error } = await supabase
          .from("provider_ratings" as any)
          .insert({
            session_id: sessionId,
            member_id: user.id,
            provider_id: providerId,
            rating,
            comment: comment.trim() || null,
          } as any);
        if (error) throw error;
      }
    },
    onSuccess: () => {
      toast.success(existingRating ? "Rating updated!" : "Thank you for your feedback!");
      queryClient.invalidateQueries({ queryKey: ["member-ratings"] });
      onOpenChange(false);
    },
    onError: (err: any) => {
      toast.error(err.message || "Failed to submit rating");
    },
  });

  const ratingLabels = ["", "Poor", "Fair", "Good", "Very Good", "Excellent"];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="text-xl">
            {existingRating ? "Update Your Rating" : "Rate Your Visit"}
          </DialogTitle>
          <DialogDescription>
            How was your experience with {providerName}?
          </DialogDescription>
        </DialogHeader>

        <div className="py-4 space-y-6">
          {/* Star Rating */}
          <div className="flex flex-col items-center gap-3">
            <div className="flex gap-1.5">
              {[1, 2, 3, 4, 5].map((star) => (
                <button
                  key={star}
                  type="button"
                  className="p-1 transition-transform hover:scale-110 focus:outline-none"
                  onMouseEnter={() => setHoveredStar(star)}
                  onMouseLeave={() => setHoveredStar(0)}
                  onClick={() => setRating(star)}
                >
                  <Star
                    className={cn(
                      "h-9 w-9 transition-colors",
                      (hoveredStar || rating) >= star
                        ? "fill-amber-400 text-amber-400"
                        : "text-muted-foreground/30"
                    )}
                  />
                </button>
              ))}
            </div>
            {(hoveredStar || rating) > 0 && (
              <p className="text-sm font-medium text-foreground">
                {ratingLabels[hoveredStar || rating]}
              </p>
            )}
          </div>

          {/* Comment */}
          <div>
            <Label htmlFor="rating-comment" className="text-sm font-medium">
              Comments (optional)
            </Label>
            <Textarea
              id="rating-comment"
              placeholder="Share any feedback about your visit..."
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              className="mt-2 resize-none"
              rows={3}
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            onClick={() => submitRating.mutate()}
            disabled={rating === 0 || submitRating.isPending}
          >
            {submitRating.isPending
              ? "Submitting..."
              : existingRating
              ? "Update Rating"
              : "Submit Rating"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
